import React, { useState, useRef, useContext } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Grid } from "@mui/material";
import EMPButton from "../../shared/components/EMPButton/EMPButton";
import EMPDataCard from "../../shared/components/EMPDataCard/EMPDataCard";
import EMPAccordion from "../../shared/components/EMPAccordion/EMPAccordion";
import { formatVehicleTrim } from "../../utils/EmpUtil";
import { useQuery } from "@tanstack/react-query";
import CustomerReservationsClient from "../../clients/CustomerReservationsClient";
import { base64OpenInNewTab } from "../../utils/FileDownloadUtil";
import EMPLoader from "../../shared/components/EMPLoader/EMPLoader";
import MotionChevronRight from "../../shared/Icons/MotionChevronRight";
import MotionChevronRightCircle from "../../shared/Icons/MotionChevronRightCircle";
import ProtectionPlan from "./ProtectionPlan";
import { ModalContext } from "../../shared/components/EMPModal/ModalContext";
import SimpleErrorMessage from "../../shared/components/EMPModal/ModalFragments/SimpleErrorMessage";
import { useTranslation } from "react-i18next";
import { trackOrderDetailsPageAction } from "../../shared/services/analytics/OrderDetailsPageAnalytics";
import { UserContext } from "../../shared/context/userContext";

const LincolnVehicleCard = ({ data, testId, minHeight = 64 }) => (
  <Box
    component="dl"
    data-testid={testId}
    sx={{
      m: 0,
      p: "10px",
      minHeight,
      boxSizing: "border-box",
      border: "1px solid #B2B2B2",
      borderRadius: "8px",
      backgroundColor: "#FFFFFF",
      display: "grid",
      gridTemplateColumns: {
        xs: "minmax(0, 1fr)",
        sm: "repeat(2, minmax(0, 1fr))",
        md: "repeat(6, minmax(0, 1fr))",
      },
      gap: "16px",
      alignItems: "start",
    }}
  >
    {data.map((item) => (
      <Box key={item.heading} sx={{ minWidth: 0 }}>
        <Typography component="dt" variant="caption" sx={{ mb: 1 }}>
          {item.heading}
        </Typography>
        <Box component="dd" sx={{ m: 0 }}>
          <Typography
            component="div"
            variant="body2"
            sx={{ overflowWrap: "anywhere" }}
          >
            {item.content}
          </Typography>
        </Box>
      </Box>
    ))}
  </Box>
);

LincolnVehicleCard.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      heading: PropTypes.node.isRequired,
      content: PropTypes.node.isRequired,
    })
  ).isRequired,
  testId: PropTypes.string.isRequired,
  minHeight: PropTypes.number,
};

const extractVehicleDetails = (content) => {
  const { vinNumber, trim, color } = content?.customerReservation || {};
  return { vin: vinNumber, trim, color };
};

const Vehicle = ({ content, orderId }) => {
  const [expandProtectionPlans, setExpandProtectionPlans] = useState(true);
  const vehicleDetails = extractVehicleDetails(content);
  const { handleModal } = useContext(ModalContext);
  const vehicleContainer = useRef(null);
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const { dealerId } = user;
  const isLincolnBrand = user.brand === "L";
  const customerReservation = content?.customerReservation;

  const getVehicleName = isLincolnBrand
    ? [customerReservation?.modelYear, customerReservation?.nameplate]
        .filter(Boolean)
        .join(" ") || "--"
    : `${customerReservation?.modelYear} ${customerReservation?.nameplate}`;

  const { isFetching, refetch } = useQuery({
    queryKey: ["windowSticker"],
    queryFn: () =>
      CustomerReservationsClient.getWindowSticker(
        dealerId,
        customerReservation?.vinNumber
      ),
    staleTime: Infinity,
    enabled: false,
    retry: 1,
    select: ({ data }) => data,
  });

  const handleRefetchAndOpenInNewTab = async () => {
    const { data: windowStickerBlob } = await refetch();
    if (windowStickerBlob) {
      base64OpenInNewTab(windowStickerBlob, "application/pdf");
    } else {
      handleModal(
        t("common.somethingWentWrong"),
        <SimpleErrorMessage
          error={t("common.errorMessage3", { item: "Window Sticker" })}
        />
      );
    }
  };

  const vehicleData = [
    {
      heading: t("vehicle.vehicleInfo.fields.0"),
      content: vehicleDetails.vin || "--",
    },
    {
      heading: t("vehicle.vehicleInfo.fields.1"),
      content: formatVehicleTrim(vehicleDetails.trim ?? null) || "--",
    },
    {
      heading: t("vehicle.vehicleInfo.fields.2"),
      content: vehicleDetails.color || "--",
    },
  ];

  return (
    <Box
      display="grid"
      gridTemplateColumns="repeat(12, 1fr)"
      columnGap={4}
      rowGap={6}
      sx={{ mt: isLincolnBrand ? 0 : 5, mb: 11 }}
      ref={vehicleContainer}
    >
      <Box
        gridColumn={{
          xs: "span 12",
        }}
      >
        <Grid
          container
          alignItems="center"
          rowGap={isLincolnBrand ? 2 : undefined}
        >
          <Grid item>
            <Typography
              variant="h3"
              sx={{
                color: "text.primary",
                ...(isLincolnBrand && {
                  fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
                  fontSize: "24px",
                  fontWeight: 600,
                  lineHeight: "32px",
                }),
              }}
            >
              {getVehicleName}
            </Typography>
          </Grid>
          <Grid item sx={{ ml: 5 }}>
            {isFetching ? (
              <EMPLoader loadingMessage="Loading Window Sticker" />
            ) : (
              <EMPButton
                variant="contained"
                sx={
                  isLincolnBrand
                    ? {
                        color: "#FFFFFF",
                        backgroundColor: "#485B65",
                        border: "none",
                        borderRadius: "4px",
                        minWidth: 0,
                        minHeight: "40px",
                        padding: "8px 20px",
                        fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
                        fontWeight: 600,
                        "&:hover": { backgroundColor: "#3A4A53" },
                        "&.Mui-focusVisible": {
                          outline: "2px solid #485B65",
                          outlineOffset: 3,
                        },
                      }
                    : undefined
                }
                onClick={() => {
                  handleRefetchAndOpenInNewTab();
                  trackOrderDetailsPageAction(
                    undefined,
                    "vehicle:see window sticker",
                    orderId,
                    customerReservation?.modelYear,
                    customerReservation?.nameplate,
                    user
                  );
                }}
                rightIcon={
                  isLincolnBrand ? (
                    <MotionChevronRightCircle sx={{ fontSize: "20px" }} />
                  ) : (
                    <MotionChevronRight />
                  )
                }
              >
                {t(
                  isLincolnBrand
                    ? "vehicle.seeWindowSticker"
                    : "common.buttonCtas.viewWindowSticker"
                )}
              </EMPButton>
            )}
          </Grid>
        </Grid>
      </Box>

      <Box
        gridColumn={{
          xs: "span 12",
        }}
      >
        {isLincolnBrand ? (
          <LincolnVehicleCard data={vehicleData} testId="vehicle-data" />
        ) : (
          <EMPDataCard data={vehicleData} hasBackground testId="vehicle-data" />
        )}
      </Box>
      {isLincolnBrand ? (
        <Box
          component="section"
          data-testid="vehicle-protection-plans"
          gridColumn="span 12"
          sx={{ mt: 8 }}
        >
          <Typography
            variant="h4"
            sx={{
              mb: 6,
              fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
              fontSize: "24px",
              fontWeight: 600,
              lineHeight: "32px",
            }}
          >
            {t("vehicle.protectionPlans.heading")}
          </Typography>
          <ProtectionPlan
            protectionPlansData={customerReservation?.protectionPlansData}
            isLincolnBrand
          />
        </Box>
      ) : (
        <EMPAccordion
          heading={t("vehicle.protectionPlans.heading")}
          expanded={expandProtectionPlans}
          onChange={() => setExpandProtectionPlans(!expandProtectionPlans)}
        >
          <ProtectionPlan
            protectionPlansData={customerReservation?.protectionPlansData}
          />
        </EMPAccordion>
      )}
    </Box>
  );
};

Vehicle.propTypes = {
  content: PropTypes.shape({
    customerReservation: PropTypes.shape({
      vinNumber: PropTypes.string,
      trim: PropTypes.string,
      color: PropTypes.string,
      modelYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      nameplate: PropTypes.string,
      protectionPlansData: PropTypes.shape({
        protectionPlans: PropTypes.arrayOf(PropTypes.object),
      }),
    }).isRequired,
  }).isRequired,
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default Vehicle;
